/**
 * Cellule « Dernière sauvegarde » des cartes portefeuille + remontage UI Hybride
 * après chaque bascule d’entreprise.
 */
import type { AppContext } from '@application/context/AppContext';
import { resolveActiveTenantId } from '@application/licence/ActiveTenant';
import { readHybridVaultStatus } from '@application/backup/HybridVaultService';
import { esc } from '@presentation/shell/format';
import { logger } from '@infra/telemetry/Logger';

export interface PortfolioHybridBackupHtmlOpts {
  /** true = dossier actuellement ouvert sur le poste. */
  isActive: boolean;
}

function formatLastBackupAt(iso: string | null): string {
  if (!iso) return 'Jamais';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Jamais';
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * HTML d’une cellule KPI « Dernière sauvegarde » pour une carte portefeuille.
 * Les entreprises non ouvertes ne sont pas sauvegardables : hint si jamais
 * sauvegardée ou si le dossier n’est pas actif.
 */
export function renderPortfolioHybridBackupHtml(
  tenantId: string,
  opts: PortfolioHybridBackupHtmlOpts,
): string {
  let status: { lastSentAt: string | null };
  try {
    status = readHybridVaultStatus(tenantId);
  } catch {
    // Stockage navigateur bloqué : afficher « Jamais » plutôt que casser le portefeuille.
    status = { lastSentAt: null };
  }
  const neverSaved = !status.lastSentAt;
  const value = formatLastBackupAt(status.lastSentAt);
  const showOpenHint = neverSaved || !opts.isActive;
  const hint = showOpenHint
    ? `<span class="pf-hybrid-hint" style="display:block;font-size:10px;color:var(--gris);font-weight:500;text-transform:none;letter-spacing:0;margin-top:4px;line-height:1.3;">à sauvegarder à l'ouverture</span>`
    : '';
  const ariaExtra = showOpenHint ? ' — à sauvegarder à l\'ouverture' : '';

  return `<div class="pf-hybrid-backup" style="grid-column:1/-1;" role="status" aria-label="${esc(`Dernière sauvegarde : ${value}${ariaExtra}`)}" data-tenant-id="${esc(tenantId)}">
    <span class="pf-k" style="font-size:13px;">${esc(value)}</span>
    <span class="pf-l">Dernière sauvegarde</span>
    ${hint}
  </div>`;
}

let hybridUiDisposer: (() => void) | null = null;

export interface HybridBackupShellLike {
  root: HTMLElement;
  ctx: AppContext;
}

/**
 * Après bascule d’entreprise : nettoie l’UI Hybride précédente puis remonte
 * consentement / bannière / assistant pour le tenant actif (`mountHybridBackupUi`).
 */
export async function ensureHybridBackupUiAfterTenantSwitch(
  shellLike: HybridBackupShellLike,
): Promise<void> {
  const tenantId = resolveActiveTenantId(shellLike.ctx);
  if (!tenantId) return;

  if (hybridUiDisposer) {
    try {
      hybridUiDisposer();
    } catch {
      /* best-effort */
    }
    hybridUiDisposer = null;
  }

  try {
    const { mountHybridBackupUi } = await import(
      '@presentation/views/parametres/hybrid/HybridBackupUiBootstrap'
    );
    hybridUiDisposer = await mountHybridBackupUi({
      ctx: shellLike.ctx,
      tenantId,
      root: shellLike.root,
    });
  } catch (err) {
    logger.warn({
      event: 'hybrid_backup_ui_remount_after_switch_failed',
      tenant_id: tenantId,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
